import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useLocale } from '../contexts/LocaleContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { Input } from './ui/input';
import { cn } from '@/lib/utils';
import { API_BASE } from '../config';
import { useToast } from '../contexts/ToastContext';
import { Download, Loader2, Eye, EyeOff, Plus, Sun, Moon, Monitor } from 'lucide-react';
import { RotatingDeleteIcon } from './RotatingDeleteIcon';
import { WritingSettings } from './WritingSettings';
import { LanguageSelect } from './LanguageSelect';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogPrimaryShortcut,
} from './ui/dialog';
import { APP_HEADER_HEIGHT } from '../constants/layout';
import { getVerticalScrollParent, scrollElementToTop } from '../utils/scroll-element-to-top';

const NOTIFICATION_SETTINGS_HASH = '#notification-settings';
const NOTIFICATION_SETTINGS_SCROLL_MARGIN = `calc(${APP_HEADER_HEIGHT} + 1rem)`;
const APP_HEADER_OFFSET_PX = Number.parseInt(APP_HEADER_HEIGHT, 10) || 64;


export const Settings: React.FC = () => {
  const location = useLocation();
  const { user, token } = useAuth();
  const { settings, updateSetting } = useSettings();
  const { t } = useLocale();
  const { theme, setTheme } = useTheme();
  const { showToast, showErrorToast } = useToast();
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (location.hash !== NOTIFICATION_SETTINGS_HASH) {
      return;
    }
    const element = document.getElementById('notification-settings');
    if (!element) {
      return;
    }
    let secondFrameId = 0;
    const firstFrameId = window.requestAnimationFrame(() => {
      secondFrameId = window.requestAnimationFrame(() => {
        const scrollParent = getVerticalScrollParent(element);
        scrollElementToTop(element, {
          behavior: 'smooth',
          offsetPx: scrollParent ? 0 : APP_HEADER_OFFSET_PX,
        });
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrameId);
      window.cancelAnimationFrame(secondFrameId);
    };
  }, [location.hash]);

  const handleChangePassword = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    if (newPassword !== confirmPassword) {
      const errorMsg = t('passwords_not_match');
      setPasswordError(errorMsg);
      showErrorToast(errorMsg);
      return;
    }

    if (newPassword.length < 8) {
      const errorMsg = t('password_too_short');
      setPasswordError(errorMsg);
      showErrorToast(errorMsg);
      return;
    }

    if (!token) {
      const errorMsg = t('failed_to_change_password');
      setPasswordError(errorMsg);
      showErrorToast(errorMsg);
      return;
    }

    setIsChangingPassword(true);

    try {
      const response = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        let errorMsg: string;
        if (errorData.error === 'Invalid current password') {
          errorMsg = t('invalid_current_password');
        } else {
          errorMsg = errorData.error || t('failed_to_change_password');
        }
        setPasswordError(errorMsg);
        showErrorToast(errorMsg);
        setIsChangingPassword(false);
        return;
      }

      const successMsg = t('password_changed_successfully');
      setPasswordSuccess(successMsg);
      showToast(successMsg);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setIsChangingPassword(false);
    } catch (error) {
      const errorMsg = t('failed_to_change_password');
      setPasswordError(errorMsg);
      showErrorToast(errorMsg);
      setIsChangingPassword(false);
    }
  };


  const handleSettingChange = async <K extends keyof typeof settings>(
    key: K,
    value: typeof settings[K]
  ): Promise<void> => {
    try {
      await updateSetting(key, value);
      showToast(t('settings_saved') || 'Settings saved successfully');
    } catch (error) {
      console.error('Error updating setting:', error);
      showErrorToast(t('settings_save_error') || 'Failed to save settings');
    }
  };

  const handleExportData = async (): Promise<void> => {
    if (!token) {
      showErrorToast(t('privacy_export_error_not_logged_in') || 'You must be logged in to export your data');
      return;
    }

    setIsExporting(true);
    try {
      const response = await fetch(`${API_BASE}/api/privacy/export`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export data');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scribane-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showToast(t('privacy_export_success') || 'Your data has been exported successfully');
    } catch (error) {
      console.error('Error exporting data:', error);
      showErrorToast(t('privacy_export_error') || 'Failed to export your data');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async (): Promise<void> => {
    if (!token) {
      showErrorToast(t('privacy_delete_error_not_logged_in') || 'You must be logged in to delete your account');
      return;
    }

    setIsDeleting(true);
    try {
      const response = await fetch(`${API_BASE}/api/privacy/delete-account`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete account');
      }

      showToast(t('privacy_delete_success') || 'Your account has been deleted');
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      showErrorToast(t('privacy_delete_error') || 'Failed to delete your account');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const sectionClassName = 'mb-5 md:mb-8';
  const sectionTitleClassName = 'mb-3 text-xl font-semibold md:mb-4';
  const cardClassName = cn('relative border-0 shadow-none rounded-[10px]');
  const cardBodyClassName = 'p-4 md:px-8 md:py-6';
  const cardStyle = { border: '1px solid var(--border)', backgroundColor: 'var(--card)' };

  return (
    <>
      <div
        className={cn(
          'mx-auto max-w-[900px] p-0 md:p-8',
          location.hash === NOTIFICATION_SETTINGS_HASH && 'pb-[70vh]'
        )}
      >
        {user && (
          <div className={sectionClassName}>
            <h2 className={sectionTitleClassName}>{t('account_information')}</h2>
            <Card className={cardClassName} style={cardStyle}>
              <div className={cardBodyClassName}>
                <div style={{ marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>{t('email')}: </span>
                  <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>{user.email}</span>
                </div>
                <div style={{ marginTop: '1rem' }}>
                  <Link to="/desk/usage-and-billing">
                    <Button variant="outline" style={{ width: '100%' }}>
                      {t('usage_and_billing')}
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          </div>
        )}
        <div className={sectionClassName}>
          <Card className={cardClassName} style={cardStyle}>
            <WritingSettings />
          </Card>
        </div>
        <div
          id="notification-settings"
          className={sectionClassName}
          style={{ scrollMarginTop: NOTIFICATION_SETTINGS_SCROLL_MARGIN }}
        >
          <h2 className={sectionTitleClassName}>{t('notification_settings')}</h2>
          <Card className={cardClassName} style={cardStyle}>
            <div className={cardBodyClassName}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={settings.email_notifications_disabled}
                  onChange={(e) => handleSettingChange('email_notifications_disabled', e.target.checked)}
                  style={{
                    width: '1.25rem',
                    height: '1.25rem',
                    cursor: 'pointer',
                  }}
                />
                <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>{t('email_notifications_disabled')}</span>
              </label>
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', marginLeft: '2rem' }}>
                {t('email_notifications_disabled_help')}
              </p>
            </div>
          </Card>
        </div>
        <div className={sectionClassName}>
          <h2 className={sectionTitleClassName}>{t('appearance_settings')}</h2>
          <Card className={cardClassName} style={cardStyle}>
            <div className={cardBodyClassName}>
              <div style={{ marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
                {t('theme')}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <Button
                  variant={theme === 'light' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTheme('light')}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}
                >
                  <Sun className="h-4 w-4" />
                  {t('theme_light')}
                </Button>
                <Button
                  variant={theme === 'dark' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTheme('dark')}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}
                >
                  <Moon className="h-4 w-4" />
                  {t('theme_dark')}
                </Button>
                <Button
                  variant={theme === 'system' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTheme('system')}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}
                >
                  <Monitor className="h-4 w-4" />
                  {t('theme_system')}
                </Button>
              </div>
              <div style={{ marginTop: '1.25rem', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
                {t('language')}
              </div>
              <LanguageSelect />
            </div>
          </Card>
        </div>
        {user && (
          <div className={sectionClassName}>
            <h2 className={sectionTitleClassName}>{t('security_settings')}</h2>
            <Card className={cardClassName} style={cardStyle}>
              <div className={cardBodyClassName}>
                <form onSubmit={handleChangePassword}>
                  <div style={{ marginBottom: '1rem' }}>
                    <label
                      htmlFor="current-password"
                      style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#374151',
                      }}
                    >
                      {t('current_password')}
                    </label>
                    <div style={{ position: 'relative', width: '100%' }}>
                      <Input
                        id="current-password"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                        disabled={isChangingPassword}
                        style={{ width: '100%', paddingRight: '2.5rem' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        style={{
                          position: 'absolute',
                          right: '0.75rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#6b7280',
                        }}
                        tabIndex={-1}
                      >
                        {showCurrentPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label
                      htmlFor="new-password"
                      style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#374151',
                      }}
                    >
                      {t('new_password')}
                    </label>
                    <div style={{ position: 'relative', width: '100%' }}>
                      <Input
                        id="new-password"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        disabled={isChangingPassword}
                        minLength={8}
                        style={{ width: '100%', paddingRight: '2.5rem' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        style={{
                          position: 'absolute',
                          right: '0.75rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#6b7280',
                        }}
                        tabIndex={-1}
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label
                      htmlFor="confirm-password"
                      style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#374151',
                      }}
                    >
                      {t('confirm_new_password')}
                    </label>
                    <div style={{ position: 'relative', width: '100%' }}>
                      <Input
                        id="confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={isChangingPassword}
                        minLength={8}
                        style={{ width: '100%', paddingRight: '2.5rem' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                          position: 'absolute',
                          right: '0.75rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#6b7280',
                        }}
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  {passwordError && (
                    <div
                      style={{
                        marginBottom: '1rem',
                        padding: '0.75rem',
                        backgroundColor: '#fee2e2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        color: '#991b1b',
                        fontSize: '0.875rem',
                      }}
                    >
                      {passwordError}
                    </div>
                  )}
                  {passwordSuccess && (
                    <div
                      style={{
                        marginBottom: '1rem',
                        padding: '0.75rem',
                        backgroundColor: '#d1fae5',
                        border: '1px solid #a7f3d0',
                        borderRadius: '6px',
                        color: '#065f46',
                        fontSize: '0.875rem',
                      }}
                    >
                      {passwordSuccess}
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword}
                    style={{ width: '100%' }}
                  >
                    {isChangingPassword ? t('saving') : t('change_password')}
                  </Button>
                </form>
              </div>
            </Card>
          </div>
        )}
        <div className={sectionClassName}>
          <h2 className={sectionTitleClassName}>{t('privacy_policy_your_rights_title')}</h2>
          <Card className={cardClassName} style={cardStyle}>
            <div className={cardBodyClassName}>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
                {t('privacy_policy_your_rights_content')}
              </p>
              {user && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <Button
                    onClick={handleExportData}
                    disabled={isExporting}
                    variant="outline"
                    style={{ alignSelf: 'flex-start' }}
                  >
                    {isExporting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('privacy_exporting')}
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        {t('privacy_export_data')}
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={isDeleting}
                    variant="destructive"
                    className="group inline-flex items-center gap-1.5 self-start"
                    style={{ color: 'white' }}
                  >
                    {isDeleting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" style={{ color: 'white' }} />
                        <span style={{ color: 'white' }}>{t('privacy_deleting')}</span>
                      </>
                    ) : (
                      <>
                        <RotatingDeleteIcon className="text-white" />
                        <span style={{ color: 'white' }}>{t('privacy_delete_account')}</span>
                      </>
                    )}
                  </Button>
                </div>
              )}
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
                <Link to="/privacy">
                  <Button variant="outline" style={{ width: '100%' }}>
                    {t('privacy_policy_title')}
                  </Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('privacy_delete_dialog_title')}</DialogTitle>
              <DialogDescription>{t('privacy_delete_dialog_description')}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <CancelButton onClick={() => setShowDeleteDialog(false)} />
              <Button
                onClick={handleDeleteAccount}
                variant="destructive"
                dialogPrimary
                className="group inline-flex items-center gap-1.5"
              >
                <RotatingDeleteIcon />
                {t('privacy_delete_confirm')}
                <DialogPrimaryShortcut className="ml-0.5" />
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

